import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "design-system/atoms/Accordion"
import { Button } from "design-system/atoms/Button"
import { Textarea } from "design-system/atoms/Textarea"
import { VaultVariableAutocomplete } from "modules/shared/components/VaultVariableAutocomplete"
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { cn } from "design-system/utils"
import { Plus } from "lucide-react"
import { Controller, FieldValues, Path, UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { InstructionCapabilityConfigSchema } from "schema"
import { VariableOption } from "../../../hooks/useCapabilityVariables"
import { FieldsForm } from "../FieldsForm"

type Props<T extends FieldValues> = {
  form: UseFormReturn<T>
  translationPrefix?: string
  variables?: VariableOption[]
}

export function ActivationAccordion<T extends FieldValues>({
  form,
  translationPrefix = "webhookConfig",
  variables = [],
}: Props<T>) {
  const { t } = useTranslation()

  return (
    <Accordion
      type="single"
      collapsible
      defaultValue="activation"
      className={cn(
        "border rounded-lg bg-secondary",
        (form.formState.errors.activationPrompt || form.formState.errors.dataToExtract) &&
          "border-destructive",
      )}
    >
      <AccordionItem value="activation">
        <AccordionTrigger className="text-lg">
          {t(`capabilities.${translationPrefix}.activation`)}
        </AccordionTrigger>
        <AccordionContent className="flex flex-col gap-5">
          <FormField
            control={form.control}
            name={"activationPrompt" as Path<T>}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t(`capabilities.${translationPrefix}.activationPrompt.label`)}
                </FormLabel>
                <FormDescription className="text-sm text-muted-foreground">
                  {t(`capabilities.${translationPrefix}.activationPrompt.description`)}
                </FormDescription>
                <FormControl>
                  <VaultVariableAutocomplete variables={variables}>
                    <Textarea
                      placeholder={t(
                        `capabilities.${translationPrefix}.activationPrompt.placeholder`,
                      )}
                      rows={4}
                      maxLength={
                        InstructionCapabilityConfigSchema.shape.activationPrompt.maxLength ??
                        undefined
                      }
                      className="min-h-32"
                      {...field}
                    />
                  </VaultVariableAutocomplete>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Controller
            control={form.control}
            name={"dataToExtract" as Path<T>}
            render={({ field }) => (
              <FieldsForm
                label={t(`capabilities.${translationPrefix}.dataToExtract.label`)}
                description={t(`capabilities.${translationPrefix}.dataToExtract.description`)}
                action={
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() =>
                      field.onChange([
                        ...(field.value || []),
                        {
                          name: "",
                          type: "string" as const,
                          description: "",
                          required: true,
                        },
                      ])
                    }
                    className="w-fit"
                  >
                    <Plus className="size-4" />
                    {t("capabilities.webhookConfig.fieldsForm.addField")}
                  </Button>
                }
                value={field.value}
                onChange={field.onChange}
                required={false}
                minFields={0}
                error={form.formState.errors.dataToExtract?.message as string}
                mode="schema"
                variables={variables}
              />
            )}
          />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
